<template>
  <div class="container-fluid">
    <div class="row justify-content-center">
      <div class="col-12 col-lg-2">
        <QuizInfo v-bind:quizID="quizID" class="mb-3" />
        <b-card class="mb-3" v-if="showSurveyCard || venue != null">
          <template v-slot:header>Surveys</template>
          <b-button
            class="mb-2"
            v-if="venue && myTeamOffline"
            variant="primary"
            block
            @click="setTeamOffline(false)"
          >
            <b-icon-cup-straw class="mr-1" />Your team is at <b>{{ venue }}</b>
          </b-button>
          <b-button
            class="mb-2"
            v-if="venue && !myTeamOffline"
            block
            @click="setTeamOffline(true)"
          >
            Your team is playing online
          </b-button>
          <div v-for="q in surveys" :key="q.id">
            <SurveyAnswerButton
              v-if="q.active || hasAnswer(q.id)"
              :question="q"
              :answer="answers[q.id] ? answers[q.id][activeTeamID] : null"
              :class="{
                'blink-button': q.active && !hasAnswer(q.id)
              }"
              class="mb-1"
              @click="showSurveyAnswer"
            />
          </div>
        </b-card>
        <QuizScoring
          v-bind:class="{ 'd-none': teamLeft }"
          v-bind:quizID="quizID"
          class="mb-3"
        />
      </div>
      <div class="col-12 col-lg-9">
        <ScoreTable
          v-bind:quizID="quizID"
          spectate="false"
          :externalFields="externalFields"
          @update:venue="venue = $event"
          @update:offlineTeam="myTeamOfflineUpdate"
        />
      </div>
    </div>
    <DialogInfo v-model="infoVisible">
      <p>{{ infoMessage }}</p>
    </DialogInfo>
    <DialogError v-model="errorVisible">
      <p>{{ errorMessage }}</p>
    </DialogError>
    <SurveyAnswerDialog
      v-model="answerDialogVisible"
      :question="selectedQuestion"
      :rounds="rounds"
      @answer-submit="submitAnswer"
    />
    <TopQuizChat :quizID="quizID" :quizHost="quizHost" />
  </div>
</template>

<script>
import TopQuizChat from "@/components/TopQuizChat";
import QuizInfo from "@/components/QuizInfo";
import ScoreTable from "@/components/ScoreTable";
import QuizScoring from "@/components/QuizScoring";
import DialogInfo from "@/components/DialogInfo";
import DialogError from "@/components/DialogError";
import SurveyAnswerDialog from "@/components/SurveyAnswerDialog";
import SurveyAnswerButton from "@/components/SurveyAnswerButton";
import { mapGetters } from "vuex";

export default {
  name: "Play",
  components: {
    QuizInfo,
    ScoreTable,
    QuizScoring,
    DialogError,
    DialogInfo,
    SurveyAnswerDialog,
    SurveyAnswerButton,
    TopQuizChat
  },
  computed: {
    ...mapGetters("teamStore", { activeTeamID: "activeTeamID" }),
    showSurveyCard() {
      for (let i = 0; i < this.surveys.length; i++) {
        const survey = this.surveys[i];
        if (this.hasAnswer(survey.id)) {
          return true;
        }

        if (survey.active == 1) {
          return true;
        }
      }

      return false;
    },
    externalFields() {
      var fields = [];
      this.surveys.forEach(survey => {
        if (!survey.showInTable || survey.public != 1) {
          return;
        }

        fields.push({
          key: "ext_" + survey.id,
          label: survey.title,
          data: teamID => {
            if (!this.answers[survey.id]) {
              return null;
            }
            return this.answers[survey.id][teamID];
          }
        });
      });
      return fields;
    }
  },
  data() {
    return {
      quizID: 0,
      infoMessage: "",
      infoVisible: false,
      errorMessage: "",
      errorVisible: false,
      teamLeft: false,
      answerDialogVisible: false,
      selectedQuestion: null,
      rounds: [],
      surveys: [],
      answers: {},
      quizHost: null,
      venue: null,
      myTeamOffline: false
    };
  },
  created() {
    this.quizID = this.$route.params.quizID;

    this.$topquiz.on(this, "teamLeft", params => {
      var teamIDs = params[0];
      if (teamIDs.includes(this.activeTeamID)) {
        this.infoMessage =
          "Your team has left this quiz. You can no longer score here.";
        this.infoVisible = true;
        this.teamLeft = true;
      }
    });

    this.$topquiz.on(this, "team_joined", params => {
      var teamID = params[0];
      if (teamID == this.activeTeamID) {
        this.infoMessage =
          "Your team has joined this quiz. You can score again.";
        this.infoVisible = true;
        this.teamLeft = false;
      }
    });

    this.$topquiz.on(this, "getSurveyData_success", params => {
      this.surveys = params[0];

      this.$topquiz.sendCommand(
        "getSurveyAnswers",
        this.$topquiz.SessionKey,
        this.activeTeamID,
        this.quizID
      );
    });

    this.$topquiz.on(this, "questionAsked", params => {
      this.surveys.push(params[0]);
    });

    this.$topquiz.on(this, "questionStatusChanged", params => {
      var question = params[0];
      if (question.active) {
        this.questionSetActive(question);
        return;
      }

      if (
        this.selectedQuestion &&
        this.selectedQuestion.id == question.id &&
        this.answerDialogVisible
      ) {
        this.answerDialogVisible = false;
        this.infoMessage =
          "Sorry, you were too slow, your host just disabled this survey.";
        this.infoVisible = true;
      }

      this.questionSetInactive(question);
    });

    this.$topquiz.on(this, "questionListedChanged", params => {
      var question = params[0];
      this.surveys.forEach(survey => {
        if (survey.id == question.id) {
          survey.showInTable = question.showInTable;
        }
      });
    });

    this.$topquiz.on(this, "answerQuestion_error", params => {
      this.errorMessage = params[0];
      this.errorVisible = true;

      this.$topquiz.sendCommand(
        "getSurveyAnswers",
        this.$topquiz.SessionKey,
        this.activeTeamID,
        this.quizID
      );
    });

    this.$topquiz.on(this, "answerQuestion_success", params => {
      this.addAnswer(params[0]);
    });

    this.$topquiz.on(this, "answerReceived", params => {
      this.addAnswer(params[0]);
    });

    this.$topquiz.on(this, "surveyBatchAnswer", params => {
      var answers = params[1];
      answers.forEach(a => {
        this.addAnswer(a);
      });
    });

    this.$topquiz.on(this, "getSurveyAnswers_success", params => {
      var answersByQuestion = params[0];
      Object.keys(answersByQuestion).forEach(key => {
        var answers = answersByQuestion[key];
        answers.forEach(teamAnswer => {
          this.addAnswer(teamAnswer);
        });
      });
    });

    this.$topquiz.on(this, "getQuizData_success", params => {
      this.quizHost = params[0].host;
      this.rounds = params[1];
    });

    this.$topquiz.sendCommand(
      "getSurveyData",
      this.$topquiz.SessionKey,
      this.activeTeamID,
      this.quizID
    );
  },
  destroyed() {
    this.$topquiz.unsubscribe(this);
  },
  methods: {
    setTeamOffline(offline) {
      this.$topquiz.sendCommand(
        "setOfflineTeam",
        this.$topquiz.SessionKey,
        this.quizID,
        this.activeTeamID,
        offline
      );
    },
    myTeamOfflineUpdate(offline) {
      this.myTeamOffline = offline;
    },
    questionSetActive(question) {
      // update existing
      for (let i = 0; i < this.surveys.length; i++) {
        const survey = this.surveys[i];
        if (survey.id == question.id) {
          survey.active = question.active;
          return;
        }
      }

      // or add to surveys
      this.surveys.push(question);
    },
    questionSetInactive(question) {
      this.surveys.forEach((survey, index) => {
        if (survey.id == question.id) {
          if (!survey.showInTable) {
            this.surveys.splice(index, 1);
          } else {
            survey.active = false;
          }
        }
      });
    },
    showSurveyAnswer(question) {
      if (!question.active) {
        this.errorMessage = "This question cannot be answered at the time...";
        this.errorVisible = true;
        return;
      }

      this.selectedQuestion = question;
      this.answerDialogVisible = true;
    },
    hasAnswer(questionID) {
      return (
        this.answers[questionID] && this.answers[questionID][this.activeTeamID]
      );
    },
    submitAnswer(answer) {
      this.$topquiz.sendCommand(
        "answerQuestion",
        this.$topquiz.SessionKey,
        answer
      );
    },
    addAnswer(answer) {
      if (!this.answers[answer.question_id]) {
        this.$set(this.answers, answer.question_id, {});
      }

      this.$set(
        this.answers[answer.question_id],
        answer.team_id,
        answer.answer
      );
    }
  }
};
</script>
